import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { FormControl, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { OrganizationService } from '../../services/organization.service';
import { ErrorHandlingService } from '../../services/error-handling.service';
import { ToggleService } from '../../shared/toggle-service/toggle.service';
import { UserService } from '../../services/user.service';
import { AccountService } from '../../services/account.service';
import { TitleService } from '../../services/title.service';
import { ProjectService } from '../../services/project.service';
import { TeamService } from '../../services/team.service';
import { removeTags } from '../../helpers';
import { ResourcesService } from '../../services/resources.service';
import { MetricService } from '../../services/metric.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  providers: [MetricService],
})
export class DashboardComponent implements OnInit {
  sortField = 'name';
  defaultSort: string;
  sortAscending = true;
  columnSort = {
    name: true,
    modified: false,
  };
  perPage = 10;
  page = 1;
  perPageValue: any;
  totalPages: any;
  totalProjects: any;
  projectListAPi: any;
  searchProject = '';
  allProjects: any = [];
  isUserContainProject = false;
  firstRun = true;
  organizationsList: any = [];
  teamsList: any[] = [];
  finalIndex: any;
  orgIcons = {};
  selectedOrganization: any;
  loading = true;
  selectedQuestion;
  answerFeedback;
  userAnswer;
  nextCounter = 0;
  showPublic = true;
  welcomeVideoUrl: SafeResourceUrl = '';
  publicFC = new FormControl(true);
  searchFC = new FormControl('');
  userAnswerFC = new FormControl('', [Validators.required]);
  @ViewChild('todayQuizModal') todayQuizModal: any;

  constructor(
    readonly toggleService: ToggleService,
    private ngxLoader: NgxUiLoaderService,
    private projectService: ProjectService,
    private readonly teamService: TeamService,
    private readonly organizationService: OrganizationService,
    private errorHandlingService: ErrorHandlingService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private titleService: TitleService,
    readonly userService: UserService,
    readonly accountService: AccountService,
    private resourcesService: ResourcesService,
    private metricService: MetricService,
    private modalService: NgbModal,
  ) {
    this.searchFC.valueChanges.pipe(debounceTime(600), distinctUntilChanged()).subscribe((res) => {
      this.searchProjects();
    });
    this.publicFC.valueChanges.pipe(debounceTime(600), distinctUntilChanged()).subscribe((res) => {
      if (this.publicFC.dirty) {
        this.showPublic = !this.showPublic;
        this.searchProjects();
      }
    });
    this.userAnswerFC.valueChanges.pipe(debounceTime(600), distinctUntilChanged()).subscribe((res) => {
      if (this.userAnswerFC.dirty) {
        this.userAnswer = this.userAnswerFC.value;
        this.userAnswerFC.markAsPending();
        this.resetFeedback();
      }
    });
  }

  ngOnInit(): void {
    this.defaultSort = this.sortField;
    this.titleService.setTitle(this.route.snapshot, this.router.routerState.snapshot);
    this.finalIndex = this.perPageValue = 10;
    this.loadProjects();
    this.fetchWelcomeVideoUrl();
    this.getQuizQuestions();
  }

  getTitle(planExpiration: string, PlanType: string) {
    if (!planExpiration) {
      return PlanType;
    } else if (PlanType === 'EXPIRED') {
      return 'Expired';
    } else if (PlanType === 'GRACE_PERIOD') {
      return 'Grace Period';
    } else {
      return 'Active';
    }
  }

  getServerAndUserRoleForNextQuiz() {
    return (
      this.userService.userData?.role?.toLowerCase() === 'admin' ||
      window.location.host.includes('localhost') ||
      window.location.host.includes('demo.terminologyhub.com')
    );
  }

  searchProjects() {
    this.searchProject = this.searchFC.value;
    this.page = 1;
    this.loadProjects();
  }

  getUserPlanStatus() {
    return this.userService.processUserExpiration();
  }

  /**
   * Retrieves the icon image for a given ID and stores it in the `orgIcons` object.
   * @param id - The ID of the icon to retrieve.
   */
  getIcon(id: string) {
    if (!id) return;
    this.accountService.downloadIconImage(id).subscribe(
      (data) => {
        this.orgIcons = {
          ...this.orgIcons,
          [id]: this.createImageFromBlob(data),
        };
      },
      (error) => {
        this.orgIcons = {
          ...this.orgIcons,
          [id]: null,
        };
      },
    );
  }

  /**
   * Retrieves the role of the current user in an organization.
   * @param organization - The organization object.
   * @returns The role of the current user in the organization ('(Owner)' or '(Member)').
   */
  getMyRoleInOrganization(organization: any) {
    if (organization.owners?.indexOf(this.userService.userData.userId) > -1) return '(Owner)';
    else if (organization?.members) {
      if (organization?.members?.indexOf(this.userService.userData.userId) > -1) return '(Member)';
    }
    return '';
  }

  /**
   * Retrieves the role of the current user in an team.
   * @param team - The organization object.
   * @returns The role of the current user in the team ('(Owner)' or '(Member)').
   */
  getMyRoleInTeam(team: any) {
    if (team.owners?.indexOf(this.userService.userData.userId) > -1) return '(Owner)';
    else if (team?.members) {
      if (team?.members?.indexOf(this.userService.userData.userId) > -1) return '(Member)';
    }
    return '';
  }

  /**
   * Creates a displayable image from a Blob object.
   * @param image - The Blob object representing the image data.
   * @returns The safe URL of the image that can be used in the UI.
   */
  createImageFromBlob(image: Blob) {
    try {
      if (image) {
        const unsafeImageUrl = URL?.createObjectURL(image);
        return this.sanitizer.bypassSecurityTrustUrl(unsafeImageUrl);
      }
    } catch (error) {
      return error;
    }
  }

  loadOrganizations(): void {
    let query = '';
    if (this.isActiveUserAdmin() === true) {
      query = 'NOT name:"Default Org"';
    }
    this.organizationService.getOrganizations(query, 0, 1000, '', false).subscribe(
      (data) => {
        this.organizationsList = data?.items ?? [];
        this.finalizeProjectMapping();
        for (const org of this.organizationsList) {
          if (this.orgIcons[org.id] === undefined) {
            this.getIcon(org.id);
          }
        }
      },
      (err) => {
        this.ngxLoader.stop();
      },
    );
  }

  loadProjectOrgs(): void {
    // 1. Extract unique organization IDs and cast them as strings
    const orgIdsInView = this.allProjects.map((project: any) => project.organizationId as string).filter((id) => !!id && !!id.trim());

    // Explicitly type the Set as a collection of strings
    const uniqueOrgIds = [...new Set<string>(orgIdsInView)];

    // 2. Identify which orgs we haven't loaded into cache yet
    const orgIdsToFetch = uniqueOrgIds.filter((id: string) => !this.organizationsList.find((org: any) => org.id === id));

    if (orgIdsToFetch.length === 0) {
      // If we already have the data, proceed to mapping
      this.finalizeProjectMapping();
      return;
    }

    // 3. Create micro-service calls for each missing org ID (explicitly typing id as string)
    const orgRequests = orgIdsToFetch.map((id: string) =>
      this.organizationService.getOrganizationById(id).pipe(
        // Catch individual errors so one failure doesn't break forkJoin
        catchError((err) => of(null)),
      ),
    );

    // 4. Execute calls in parallel
    forkJoin(orgRequests).subscribe(
      (fetchedOrgs) => {
        const validOrgs = fetchedOrgs.filter((org) => org !== null);

        // Fetch icons for new orgs
        for (const org of validOrgs) {
          if (this.isActiveUserAdmin() === true ? org.name !== 'Default Org' : true) {
            if (this.orgIcons[org.id] === undefined) {
              this.getIcon(org.id);
            }
          }
        }
      },
      (err) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(err, err?.url);
      },
    );
  }

  /**
   * Helper function to handle final mapping after organizations are loaded.
   * Separated from loadProjectOrgs to keep things clean and DRY.
   */
  private finalizeProjectMapping(): void {
    if (this.firstRun) {
      if (this.isActiveUserAdmin() === true) {
        this.isUserContainProject = this.allProjects?.length > 0 ? false : true;
      } else {
        this.isUserContainProject =
          this.allProjects?.filter((project) => project?.public !== true)?.length === 0 || this.organizationsList?.length === 0;
        if (this.isUserContainProject && this.totalProjects > 10) {
          //Check all projects, in case the first ten projects are all public
          this.projectService.getProjectsDetails('', 0, this.totalProjects).subscribe((data) => {
            const allProjs = data.items;
            this.isUserContainProject = allProjs.filter((project) => project?.public !== true)?.length === 0 || this.organizationsList?.length === 0;
          });
        }
      }
    }
    this.firstRun = false;
    if (this.organizationsList && this.organizationsList.length > 0) {
      this.allProjects = this.allProjects?.map((project) => {
        const org = this.organizationsList?.find(({ id }) => id === project.organizationId);
        return {
          ...project,
          orgName: org?.name,
          organization: org,
        };
      });
      this.loadTeams();
    } else {
      this.ngxLoader.stop();
    }
  }

  loadTeams(): void {
    this.teamService.getTeamsDetails('', 0, 1000, '', false).subscribe(
      (data) => {
        this.teamsList = data.items ?? [];

        const orgInterval = setInterval(() => {
          if (this.organizationsList && this.organizationsList.length > 0) {
            clearInterval(orgInterval);
            this.teamsList = this.teamsList?.map((team) => {
              const org = this.organizationsList?.filter(({ id }) => id == team.organizationId)[0];
              return {
                ...team,
                orgName: org?.name,
                organization: org,
              };
            });
          }
        }, 1000);
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  isActiveUserAdmin(): boolean {
    let activeUser: any = localStorage.getItem('user_data');
    activeUser = JSON.parse(activeUser);
    return activeUser ? activeUser.role?.toLowerCase() === 'admin' : false;
  }

  loadProjects(): any {
    if (this.projectListAPi) {
      this.projectListAPi.unsubscribe();
    }
    const page = this.page - 1;
    const offset = page * this.perPage;
    this.ngxLoader.start();
    let query = this.searchProject.trim();
    this.projectListAPi = this.projectService
      .getProjectsDetails(
        query ? query : '',
        offset,
        this.perPage,
        `${this.sortField},${this.defaultSort}`,
        this.sortAscending,
        this.showPublic,
        'termhub',
      )
      .subscribe(
        (data) => {
          this.totalProjects = data.total;
          this.totalPages = data.total;
          this.allProjects = data?.items ?? [];

          this.allProjects = this.allProjects.map((project) => {
            return {
              ...project,
              ...(!project?.public
                ? { description: removeTags(project.description).split('\n')[0] }
                : { description: project.description.split('\n')[0] }),
            };
          });
          this.loadProjectOrgs();
          this.loadOrganizations();
          setTimeout(() => {
            this.loading = false;
            this.ngxLoader.stop();
          }, 50);
        },
        (err) => {
          this.ngxLoader.stop();
          this.loading = false;
          this.errorHandlingService.handleError(err, err.url);
        },
      );
  }

  selectPaginationLimit(event: any) {
    this.perPage = event;
    if (event === '') {
      this.perPageValue = 'All';
      this.perPage = this.finalIndex = 100000;
    } else {
      this.finalIndex = this.perPageValue = event;
    }
    this.onPageChange(1);
  }

  onPageChange(event: any): void {
    this.page = event;
    this.loadProjects();
  }

  activateRoute(id: any) {
    this.router.navigate(['/dashboard'], { queryParams: { id: id } });
  }

  activateProjectRoute(project: any) {
    const org = this.organizationsList.find((e: any) => e?.id === project?.organizationId);
    if (project?.public === true) {
      return `/projects/${project?.uriLabel}`;
    } else {
      return `/organizations/${org?.uriLabel}/projects/${project?.uriLabel}`;
    }
  }

  activateTeamRoute(team: any) {
    return `/organizations/${team.organization?.uriLabel}/teams/${team?.uriLabel}`;
  }

  getOrgDetailsAndNavigateToTeam(team: any) {
    this.organizationService.getOrganizationById(team?.organizationId).subscribe((data) => {
      this.selectedOrganization = data;
      this.router.navigate([`/organizations/${this.selectedOrganization?.uriLabel}/teams/${team?.uriLabel}`]);
    });
  }

  tableSorter(sortBy: any) {
    this.sortField = sortBy;
    this.sortAscending = !this.columnSort[sortBy];
    this.columnSort[sortBy] = !this.columnSort[sortBy];

    this.page = 1;

    this.loadProjects();
  }

  fetchWelcomeVideoUrl(): void {
    this.resourcesService.getWelcomeVideo('AAA-WELCOME_VIDEO').subscribe((response: any) => {
      if (response) {
        const videoUrl = response?.link;
        if (videoUrl) {
          const embedUrl = videoUrl.includes('youtube.com/watch')
            ? videoUrl.replace('https://www.youtube.com/watch?v=', 'https://www.youtube.com/embed/')
            : videoUrl;
          this.welcomeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
        }
      }
    });
  }

  getDayOfYear(date: Date): number {
    const startOfYear = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - startOfYear.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  }

  getQuizQuestions(): void {
    this.resourcesService.getQuizzes().subscribe((response: any) => {
      if (response && response.items && response.items.length > 0) {
        const dayOfYear = this.getDayOfYear(new Date());
        const quizIndex = (dayOfYear + this.nextCounter) % response.total;
        this.selectedQuestion = response.items[quizIndex];
      }
    });
  }

  goToNextQuestion(): void {
    this.resetQuizState();
    this.nextCounter++;
    this.getQuizQuestions();
  }

  openQuizModal() {
    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        this.modalService.dismissAll();
      }
    });
    this.modalService.open(this.todayQuizModal, { ariaLabelledBy: 'modal-title', size: 'lg' }).result.then(
      (result) => {
        if (result === 'Cancel') {
          this.resetQuizState();
        }
      },
      (reason) => {
        this.resetQuizState();
        console.error(reason);
      },
    );
  }

  resetFeedback(): void {
    this.answerFeedback = null;
  }

  resetQuizState() {
    this.userAnswer = '';
    this.userAnswerFC.reset();
    this.answerFeedback = null;
  }

  checkAnswer(): void {
    const correctAnswer = this.selectedQuestion.answer.trim().toLowerCase();
    const userAnswerTrimmed = this.userAnswer.trim().toLowerCase();

    if (userAnswerTrimmed === correctAnswer) {
      this.answerFeedback = { correct: true };
      this.userAnswerFC.clearValidators();
      this.userAnswerFC.markAsPristine();
      this.submitMetric('correct');
    } else {
      this.answerFeedback = { correct: false };
      this.userAnswerFC.setErrors({ required: true });
      this.submitMetric('incorrect');
    }
  }

  openVideoHelp(): void {
    const videoUrl = this.selectedQuestion.url || 'https://www.youtube.com/embed/iYq3bLxfzlo';
    window.open(videoUrl, '_blank');
  }

  submitMetric(result: string): void {
    const metricData = {
      type: 'QUIZ',
      key: this.selectedQuestion.type,
      value: result,
    };
    this.metricService.submitMetric(metricData).subscribe((response) => {});
  }
}
